import { useEffect, useState } from "react";
import { TEAMS, type FeatureEntry, type Team } from "@server/rules/features";
import { activeLabels, type FormLists, type ListKind, type ValueEntry } from "@server/lists/types";
import { ApiError, useFormLists, useSaveFormLists } from "../api/client";
import { Button, ErrorBanner, Select } from "../components/ui";
import { WidePanel } from "../components/Workspace";

// A row on the page. A new row's name can still be typed; a saved one's cannot, because old tickets refer to it by name.
interface Row {
  label: string;
  team?: Team;
  retired?: boolean;
  isNew?: boolean;
}

type Rows = Record<ListKind, Row[]>;

const SECTIONS: { kind: ListKind; title: string; column: string; note: string; withTeam?: boolean }[] = [
  {
    kind: "features",
    title: "Features",
    column: "Feature",
    withTeam: true,
    note: "Each Feature's tickets go to its team. A ticket with no Feature goes to Platform. Feature is optional on the form.",
  },
  { kind: "businessUnits", title: "Business units", column: "Business Unit", note: "Required on the form: at least one must stay on offer." },
  { kind: "pricingPlans", title: "Pricing plans", column: "Pricing Plan", note: "Required on the form: at least one must stay on offer." },
];

const toRows = (lists: { features: FeatureEntry[]; businessUnits: ValueEntry[]; pricingPlans: ValueEntry[] }): Rows => ({
  features: lists.features.map((entry) => ({ ...entry })),
  businessUnits: lists.businessUnits.map((entry) => ({ ...entry })),
  pricingPlans: lists.pricingPlans.map((entry) => ({ ...entry })),
});

// What the server takes: new rows named and trimmed, blank new rows dropped.
function toUpdate(rows: Rows, version: number): FormLists & { version: number } {
  const kept = (kind: ListKind): Row[] => rows[kind].filter((row) => !(row.isNew && row.label.trim() === ""));
  return {
    features: kept("features").map((row) => ({ label: row.label.trim(), team: row.team ?? "Platform", ...(row.retired ? { retired: true } : {}) })),
    businessUnits: kept("businessUnits").map((row) => ({ label: row.label.trim(), ...(row.retired ? { retired: true } : {}) })),
    pricingPlans: kept("pricingPlans").map((row) => ({ label: row.label.trim(), ...(row.retired ? { retired: true } : {}) })),
    version,
  };
}

// The lists the engineering ticket form offers. Changes apply to the next person who opens the form, without a deploy.
export function AdminLists() {
  const stored = useFormLists();
  const save = useSaveFormLists();
  const [rows, setRows] = useState<Rows>({ features: [], businessUnits: [], pricingPlans: [] });
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (stored.data) {
      setRows(toRows(stored.data));
    }
  }, [stored.data]);

  const change = (kind: ListKind, index: number, patch: Partial<Row>): void => {
    setSaved(false);
    setRows((current) => ({ ...current, [kind]: current[kind].map((row, position) => (position === index ? { ...row, ...patch } : row)) }));
  };
  const add = (kind: ListKind): void => {
    setSaved(false);
    setRows((current) => ({ ...current, [kind]: [...current[kind], { label: "", team: kind === "features" ? "Platform" : undefined, isNew: true }] }));
  };
  const remove = (kind: ListKind, index: number): void => setRows((current) => ({ ...current, [kind]: current[kind].filter((_, position) => position !== index) }));

  // What differs from the saved lists. Nothing reaches the form until Save, so each such row says so.
  const pendingNote = (kind: ListKind, row: Row): string | undefined => {
    if (row.isNew) {
      return row.label.trim() ? "Unsaved: new" : undefined;
    }
    const was = (stored.data?.[kind] as { label: string; team?: Team; retired?: boolean }[] | undefined)?.find((entry) => entry.label === row.label);
    if (!was) {
      return undefined;
    }
    if (Boolean(was.retired) !== Boolean(row.retired)) {
      return row.retired ? "Unsaved: will retire" : "Unsaved: offer again";
    }
    return kind === "features" && was.team !== (row.team ?? "Platform") ? "Unsaved: new team" : undefined;
  };
  const changes = SECTIONS.reduce((total, section) => total + rows[section.kind].filter((row) => pendingNote(section.kind, row)).length, 0);
  const discard = (): void => {
    if (stored.data) {
      setRows(toRows(stored.data));
    }
    setSaved(false);
    save.reset();
  };

  // Leaving with changes that were never saved is how a retired value ends up still on offer.
  useEffect(() => {
    if (changes === 0) {
      return undefined;
    }
    const warn = (event: BeforeUnloadEvent): void => event.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [changes]);

  const error = save.error instanceof ApiError ? save.error.message : save.error ? "Something went wrong. Please try again." : undefined;

  return (
    <WidePanel>
      <div className="phase-head">
        <h1>Form lists</h1>
        <p>
          The Feature, Business unit and Pricing plan choices on the engineering ticket form. A value can be retired, which hides it from the form while old tickets keep it, and
          offered again later. A saved name can't be edited or deleted: retire it and add the new name.
        </p>
        <p>
          The Slack ENG Tickets Tracker List only accepts options it already has, and the Slack API can't add them. Add a new value to the matching column in the List first, spelled
          exactly the same, then add it here. Saving checks this and says which option is missing.
        </p>
      </div>

      {stored.isError && <ErrorBanner>We couldn't load the lists.</ErrorBanner>}
      {stored.data && (
        <form
          className="issue-form"
          onSubmit={(event) => {
            event.preventDefault();
            save.mutate(toUpdate(rows, stored.data.version), { onSuccess: () => setSaved(true) });
          }}
        >
          {SECTIONS.map((section) => (
            <fieldset key={section.kind} className="list-section">
              <legend>
                {section.title} <span className="record-note">({activeLabels(rows[section.kind]).length} on offer)</span>
              </legend>
              <p className="hint">{section.note}</p>
              <div className={`list-row list-head${section.withTeam ? " with-team" : ""}`} aria-hidden="true">
                <span>{section.column}</span>
                {section.withTeam && <span>Team</span>}
                <span>Status</span>
                <span />
              </div>
              {rows[section.kind].map((row, index) => (
                <div className={`list-row${section.withTeam ? " with-team" : ""}`} key={`${row.isNew ? "new" : "saved"}-${index}`}>
                  <div className="list-name">
                    {row.isNew ? (
                      <input type="text" aria-label={`${section.column} name`} placeholder={`New ${section.column.toLowerCase()}`} maxLength={100} value={row.label} onChange={(e) => change(section.kind, index, { label: e.target.value })} />
                    ) : (
                      <span className={row.retired ? "list-retired" : undefined}>{row.label}</span>
                    )}
                  </div>
                  {section.withTeam && (
                    <div>
                      <Select aria-label={`Team for ${row.label || "the new feature"}`} required value={row.team ?? "Platform"} onChange={(e) => change(section.kind, index, { team: e.target.value as Team })} options={TEAMS} />
                    </div>
                  )}
                  <div className={`list-status${pendingNote(section.kind, row) ? " is-pending" : row.retired ? " is-retired" : ""}`}>
                    {pendingNote(section.kind, row) ?? (row.retired ? "Retired" : "On offer")}
                  </div>
                  <div>
                    {row.isNew ? (
                      <Button type="button" variant="secondary" className="btn-sm" onClick={() => remove(section.kind, index)}>
                        Remove
                      </Button>
                    ) : (
                      <Button type="button" variant="secondary" className="btn-sm" onClick={() => change(section.kind, index, { retired: !row.retired })}>
                        {row.retired ? "Offer again" : "Retire"}
                      </Button>
                    )}
                  </div>
                </div>
              ))}
              <div className="list-add">
                <Button type="button" variant="secondary" className="btn-sm" onClick={() => add(section.kind)}>
                  Add a {section.column.toLowerCase()}
                </Button>
              </div>
            </fieldset>
          ))}

          {stored.data.updatedBy && (
            <p className="hint">
              Last changed by {stored.data.updatedBy}
              {stored.data.updatedAt ? ` on ${new Date(stored.data.updatedAt).toLocaleString("en-GB")}` : ""}.
            </p>
          )}
          <div className={`list-savebar${changes > 0 ? " dirty" : ""}`} role="region" aria-label="Save the lists">
            <div className="list-savebar-msg" aria-live="polite">
              {error && <span className="list-savebar-error">{error}</span>}
              {changes > 0 ? (
                <span>
                  <strong>
                    {changes} unsaved {changes === 1 ? "change" : "changes"}.
                  </strong>{" "}
                  The form is not changed until you save.
                </span>
              ) : saved ? (
                <span>Saved. The next person to open the form sees these lists.</span>
              ) : (
                <span className="record-note">No unsaved changes.</span>
              )}
            </div>
            <Button type="button" variant="secondary" disabled={changes === 0 || save.isPending} onClick={discard}>
              Discard
            </Button>
            <Button type="submit" disabled={changes === 0 || save.isPending}>
              {save.isPending ? "Saving…" : "Save changes"}
            </Button>
          </div>
        </form>
      )}
    </WidePanel>
  );
}
